/**
 * OTA-обновления мобильного приложения (@capawesome/capacitor-live-update).
 *
 * Бандлы публикует сам сайт клиента (scripts/app-updates/README.md), отдаёт
 * их Nuxt-сервер по адресу <сайт>/app-updates/
 * (server-middleware/app-updates.js). Внешних сервисов и настройки nginx
 * не нужно.
 *
 * Что делает:
 *  1. Сразу после старта зовёт ready() — без этого плагин через readyTimeout
 *     откатится на встроенный бандл. Это защита от «залили битый бандл».
 *  2. Тянет latest.json и, если там другой бандл и нативная оболочка его
 *     потянет, скачивает его (только изменившиеся файлы) и ставит следующим.
 *  3. Новый бандл применяется при следующем холодном старте — посреди сессии
 *     ничего не подменяется, корзина и формы не теряются.
 *
 * Обновляется только веб-часть. Нативный код, плагины Capacitor и права
 * через OTA не приезжают — для них нужен релиз в стор и подъём
 * appUpdates.nativeLevel в package.json.
 */
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Preferences } from '@capacitor/preferences'
import { Plugin } from '@nuxt/types'

interface ILatestBundle {
  /** null — вернуться на бандл, встроенный в сборку из стора. */
  bundleId: string | null
  nativeLevel?: number
  artifactType?: 'manifest' | 'zip'
  version?: string
  publishedAt?: string
}

const CHECK_INTERVAL = 15 * 60 * 1000
const NATIVE_LEVEL_KEY = 'app-updates.native-level.'

const loadLiveUpdate = async () =>
  (await import('@capawesome/capacitor-live-update')).LiveUpdate

/**
 * Адрес каталога обновлений — тот же сайт, что и API:
 * https://site.ru/api → https://site.ru/app-updates. Модуль при смене
 * домена переписывает адрес API в сборке, и этот адрес едет вместе с ним.
 */
const getBaseUrl = (apiUrl: string | undefined) => {
  try {
    return `${new URL(apiUrl || '').origin}/app-updates`
  } catch {
    return ''
  }
}

/**
 * Снимает service worker, оставшийся от прошлых сборок: WebView хранит его
 * регистрацию для того же origin, и он продолжает отдавать старый app shell
 * поверх свежего бандла — обновление «приезжает», но не видно.
 */
async function unregisterLegacyServiceWorkers() {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((item) => item.unregister()))
    }
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys()
      await Promise.all(keys.map((key) => caches.delete(key)))
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[app-updates] не удалось снять service worker', error)
  }
}

/**
 * Уровень нативной оболочки — какие нативные плагины и версия Capacitor в
 * неё зашиты. Знает его только встроенный бандл (у разных клиентов свои
 * versionName, сравнивать их бессмысленно), поэтому встроенный бандл
 * записывает свой уровень, а скачанные читают его.
 *
 * После обновления из стора плагин сам сбрасывается на встроенный бандл
 * (меняется versionCode), так что запись всегда соответствует оболочке.
 */
async function getShellNativeLevel(ownLevel: number) {
  const LiveUpdate = await loadLiveUpdate()
  const [{ bundleId }, { versionCode }] = await Promise.all([
    LiveUpdate.getCurrentBundle(),
    LiveUpdate.getVersionCode(),
  ])
  const key = NATIVE_LEVEL_KEY + versionCode

  if (bundleId === null) {
    await Preferences.set({ key, value: String(ownLevel) })
    return ownLevel
  }

  const { value } = await Preferences.get({ key })
  // Записи нет — считаем оболочку самой старой из тех, где OTA вообще есть:
  // в худшем случае обновление просто не придёт.
  return parseInt(value || '', 10) || 1
}

const appUpdatesPlugin: Plugin = ({ $config }) => {
  if (
    !Capacitor.isNativePlatform() ||
    !Capacitor.isPluginAvailable('LiveUpdate')
  ) {
    return
  }

  const { nativeLevel } = $config.appUpdates || {}
  const baseUrl = getBaseUrl($config.baseURL)
  if (!baseUrl) {
    return
  }

  let lastCheckAt = 0
  let checking = false
  let shellLevelPromise: Promise<number> | null = null

  const getShellLevel = () => {
    shellLevelPromise =
      shellLevelPromise || getShellNativeLevel(nativeLevel || 1)
    return shellLevelPromise
  }

  const fetchLatest = async (): Promise<ILatestBundle | null> => {
    const response = await fetch(`${baseUrl}/latest.json?t=${Date.now()}`, {
      cache: 'no-store',
    })
    if (response.status === 404) {
      // На сайте ещё ничего не опубликовано.
      return null
    }
    if (!response.ok) {
      throw new Error(`latest.json: HTTP ${response.status}`)
    }
    return response.json()
  }

  const check = async () => {
    if (checking || Date.now() - lastCheckAt < CHECK_INTERVAL) {
      return
    }
    checking = true

    try {
      const LiveUpdate = await loadLiveUpdate()
      const latest = await fetchLatest()
      lastCheckAt = Date.now()
      if (!latest || latest.bundleId === undefined) {
        return
      }

      const [current, next] = await Promise.all([
        LiveUpdate.getCurrentBundle(),
        LiveUpdate.getNextBundle(),
      ])
      if (
        latest.bundleId === current.bundleId ||
        latest.bundleId === next.bundleId
      ) {
        return
      }

      if (latest.bundleId === null) {
        // Публикацию откатили до бандла из стора.
        await LiveUpdate.setNextBundle({ bundleId: null })
        return
      }

      if ((latest.nativeLevel || 1) > (await getShellLevel())) {
        // Бандл собран под более свежую нативную оболочку — ждём релиза в сторе.
        return
      }

      await LiveUpdate.downloadBundle({
        artifactType: latest.artifactType || 'manifest',
        bundleId: latest.bundleId,
        url: `${baseUrl}/${latest.bundleId}/`,
      })
      await LiveUpdate.setNextBundle({ bundleId: latest.bundleId })
    } catch (error) {
      // Обновление — не критичный путь: приложение должно работать и без него.
      // Следующая попытка — на следующем возврате в приложение.
      lastCheckAt = 0
      // eslint-disable-next-line no-console
      console.warn('[app-updates] не удалось проверить обновление', error)
    } finally {
      checking = false
    }
  }

  const init = async () => {
    await unregisterLegacyServiceWorkers()

    try {
      const LiveUpdate = await loadLiveUpdate()
      const result = await LiveUpdate.ready()
      if (result.rollback) {
        // eslint-disable-next-line no-console
        console.warn(
          `[app-updates] откат с бандла ${result.previousBundleId}`,
          result
        )
      }
      await getShellLevel()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[app-updates] ready() не выполнен', error)
    }

    await check()

    App.addListener('resume', () => {
      check()
    })
  }

  init()
}

export default appUpdatesPlugin
